import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import ScrollToTop from "./ScrollToTop";
import SocialShareButtons from "./SocialShareButtons";
import NotFound from "./NotFound";
import SearchBar from "./SearchBar";
import MarkdownRenderer from "./MarkdownRenderer";

const BlogList = () => {
  const [articles, setArticles] = useState({});
  const [loading, setLoading] = useState(true);
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [visibleCount, setVisibleCount] = useState(3);
  const [animate, setAnimate] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    const suffix =
      day === 1 || day === 21 || day === 31
        ? "st"
        : day === 2 || day === 22
        ? "nd"
        : day === 3 || day === 23
        ? "rd"
        : "th";

    return `${day}${suffix} ${month} ${year}`;
  };

  const fetchArticles = useCallback(async () => {
    const API_URL = process.env.REACT_APP_API_URL;
    const TOKEN = process.env.REACT_APP_API_TOKEN;

    try {
      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${TOKEN}`,
        },
      });

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      const allDates = Object.keys(data.blogs || {});

      setDates(allDates);
      setArticles(data.blogs || {});

      if (allDates.length > 0) setSelectedDate(allDates[0]);
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const handleDateChange = (direction) => {
    const currentIndex = dates.indexOf(selectedDate);
    let newIndex = currentIndex;

    if (direction === "prev" && currentIndex > 0) newIndex--;
    if (direction === "next" && currentIndex < dates.length - 1) newIndex++;

    if (newIndex !== currentIndex) {
      setAnimate(true);
      setSelectedDate(dates[newIndex]);
      setVisibleCount(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => setAnimate(false), 500);
    }
  };

  const filteredArticles = selectedDate
    ? (articles[selectedDate] || []).filter((article) => {
        const term = searchTerm.toLowerCase();
        const inTitle = (article.title || "").toLowerCase().includes(term);
        const inDescription = (article.description || "").toLowerCase().includes(term);
        return inTitle || inDescription;
      })
    : [];

  const showNotFound = !loading && filteredArticles.length === 0;

  const capitalizeTitle = (title) => {
    if (!title) return "";
    return title
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const generateHashtagLink = (title) => {
    const words = title.split(" ").slice(0, 4).join(" ");
    return `https://hashtagenius.com/?q=${encodeURIComponent(words)}`;
  };

  return (
    <>
      <Helmet>
        <title>
          {selectedDate
            ? `Trending on ${formatDate(selectedDate)} | Minilo Blog`
            : "Minilo Blog - AI-Generated Trends"}
        </title>
        <meta
          name="description"
          content="Discover AI-generated trending articles daily. Stay ahead with the latest insights!"
        />
        <link rel="canonical" href="https://minilo.blog/" />
        <meta property="og:title" content="Minilo Blog - AI-Generated Trends" />
        <meta
          property="og:description"
          content="Daily AI-generated blog posts based on trending search topics worldwide."
        />
        <meta property="og:url" content="https://minilo.blog/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://minilo.blog/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Minilo Blog" />
        <meta
          name="twitter:description"
          content="Discover AI-generated trending articles daily."
        />
        <meta name="twitter:image" content="https://minilo.blog/og-image.png" />
      </Helmet>

      <ScrollToTop />

      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 lg:px-4 py-4 transition-colors duration-300">
        <div className="max-w-4xl lg:max-w-7xl mx-auto flex flex-col lg:flex-row gap-4 lg:gap-6">
          <aside className="bg-white dark:bg-gray-800 p-3 lg:p-4 w-full lg:w-1/4 order-last lg:order-first rounded transition-colors duration-300">
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-100 mb-3 lg:mb-4">
              Articles History
            </h3>
            <ul className="space-y-1.5 lg:space-y-2 text-gray-600 dark:text-gray-300">
              {dates.length > 0 ? (
                dates.map((date) => (
                  <li
                    key={date}
                    onClick={() => {
                      setAnimate(true);
                      setSelectedDate(date);
                      setVisibleCount(3);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                      setTimeout(() => setAnimate(false), 500);
                    }}
                    className={`cursor-pointer ${
                      selectedDate === date
                        ? "font-bold text-blue-800 dark:text-blue-400"
                        : "hover:text-blue-600 dark:hover:text-blue-400"
                    }`}
                  >
                    Trending on {formatDate(date)}
                  </li>
                ))
              ) : (
                <p className="text-gray-500 dark:text-gray-400">No dates available</p>
              )}
            </ul>

            <div className="mt-6 flex justify-between">
              <button
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                onClick={() => handleDateChange("prev")}
                disabled={dates.indexOf(selectedDate) === 0}
              >
                ← Previous
              </button>
              <button
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                onClick={() => handleDateChange("next")}
                disabled={dates.indexOf(selectedDate) === dates.length - 1}
              >
                Next →
              </button>
            </div>
          </aside>

          <main className="w-full lg:w-1/2 bg-white dark:bg-gray-800 p-2 lg:p-6 overflow-hidden rounded transition-colors duration-300">
            <h1 className="text-2xl lg:text-3xl font-extrabold text-gray-900 dark:text-white text-center mb-6 break-words">
              {selectedDate
                ? `Trending on ${formatDate(selectedDate)}`
                : "Latest Blog Articles"}
            </h1>

            <h3 className="text-md lg:text-lg text-center text-gray-600 dark:text-gray-300 italic mb-4">
              Minilo AI-generated articles based on trending topics.
            </h3>

            <div className="mb-6">
              <SearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>

            {showNotFound && (
              <NotFound message="No articles found for your search." />
            )}

            <div
              className={`space-y-8 transition-all duration-500 ${
                animate ? "opacity-50" : "opacity-100"
              }`}
            >
              {filteredArticles.slice(0, visibleCount).map((article, index) => (
                <article
                  key={index}
                  className="bg-white dark:bg-gray-700 shadow p-4 rounded-lg transition-colors duration-300"
                >
                  <Link
                    to={`/blog/${article.slug}`}
                    aria-label={`Read full article: ${article.title}`}
                  >
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                      {capitalizeTitle(article.title)}
                    </h2>
                  </Link>

                  <p className="text-gray-600 dark:text-gray-300 mb-3">
                    {article.description} {" "}
                    <a
                      href="https://trends.google.com/trending?geo=US"
                      className="text-blue-600 underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more
                    </a>
                  </p>

                  <MarkdownRenderer
                    content={article.content}
                    imageUrl={article.image_url}
                    title={article.title}
                  />

                  <div className="mt-3 text-sm text-blue-600 dark:text-blue-400">
                    Suggested hashtags: {" "}
                    <a
                      href={generateHashtagLink(article.title)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      View hashtags on HashTagGenius
                    </a>
                  </div>

                  <SocialShareButtons url={article.url} title={article.title} />
                </article>
              ))}
            </div>

            {visibleCount < filteredArticles.length && (
              <div className="text-center mt-8">
                <button
                  onClick={() => setVisibleCount((prev) => prev + 3)}
                  className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                  Load More
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
};

export default BlogList;
